import {
  Box,
  Button,
  Container,
  makeStyles,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useReducer, useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "../../services/api";
import { useForgotPasswordStore } from "../../store/forgotPassword";
import SnackBar from "../../pages/admin/layout/components/Snackback";
import passwordReducer, {
  PasswordsProps,
} from "../../components/ChangePassword/reducer/password.reducer";
import { ButtonColorCurrentApp, ColorDotsByCurrentApp, ColorDotsNotSelectedByCurrentApp, FontFamilyByCurrentApp, GradientBackgroundImageByCurrentApp } from "../../utils/get-app-env";

const useStyles = makeStyles((theme) => ({
  root: {
    background: GradientBackgroundImageByCurrentApp(),
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
  },
  container: {
    width: "fit-content",
    color: "#fff",
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 520,
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: theme.spacing(2),
    color: "#1e1e1e",
  },
  stepIndicator: {
    display: "flex",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    "& > div": {
      flex: 1,
      height: 8,
      borderRadius: 4,
      marginRight: theme.spacing(1),
      backgroundColor: ColorDotsNotSelectedByCurrentApp(),
      "&:nth-child(1)": {
        backgroundColor: ColorDotsByCurrentApp(),
      },
      "&:nth-child(2)": {
        backgroundColor: ColorDotsByCurrentApp(),
      },
    },
  },
  codeContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  codeInput: {
    width: 48,
    height: 48,
    fontSize: "1.5rem",
    backgroundColor: "#fff",
    border: "1px solid #000",
    borderRadius: 4,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& input": {
      textAlign: "center",
      padding: 0,
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
    },
    "& .MuiInput-underline:before": {
      borderBottom: "none", // Remove linha inferior normal
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none", // Remove linha inferior ativa
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottom: "none", // Remove ao passar o mouse
    },
  },
  button: {
    marginBottom: theme.spacing(3),
    backgroundColor: "#ccc", 
    color: "#fff",
    fontWeight: 600,
    transition: "background-color 0.3s ease",
    "&:not(:disabled)": {
      backgroundColor: ButtonColorCurrentApp(),
    },
    "&:not(:disabled):hover": {
      backgroundColor: ButtonColorCurrentApp(),
    },
  },
  resendBox: {
    border: "1px solid #fff",
    borderRadius: 4,
    padding: theme.spacing(1),
    textAlign: "center",
    color: "#ccc",
  },
  footer: {
    marginTop: theme.spacing(5),
    backgroundColor: "#2c2c2c",
    padding: theme.spacing(2),
    borderRadius: 4,
    fontSize: "0.75rem",
    textAlign: "center",
    color: "#aaa",
    "& a": {
      color: "#90caf9",
      textDecoration: "none",
      marginLeft: 4,
    },
  },
}));

const initialState: PasswordsProps = {
  currentPassword: "",
  newPassword: "",
  confirmNewPassword: "",
  errors: [],
  snackbar: {
    isOpen: false,
    message: "",
    type: "info",
  },
};

const EmailCodeVerificationPage: React.FC = () => {
  const [passwords, dispatch] = useReducer(passwordReducer, initialState);
  const classes = useStyles();
  const [timer, setTimer] = useState(60);
  const [code, setCode] = useState<string[]>(new Array(6).fill(""));
  const { email } = useForgotPasswordStore();
  const history = useHistory();
    const fontFamily = FontFamilyByCurrentApp();
  

  useEffect(() => {
    const unblock = history.block((location, action) => {
      if (action === "POP") {
        // Quando tentar voltar (POP = ação de "voltar")
        return false; // Impede de voltar
      }
    });

    return () => {
      unblock();
    };
  }, [history]);

  const handleResendCode = async () => {
    try {
      setTimer(60);

      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/forgot-password`,
        {
          email,
        }
      );
    } catch (error) {
      console.error("Erro ao reenviar código:", error);
    }
  };

  useEffect(() => {
    const countdown = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(countdown);
  }, []);

  const handleChange =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.slice(-1);
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (value && index < code.length - 1) {
        const nextInput = document.getElementById(`code-input-${index + 1}`);
        if (nextInput) {
          nextInput.focus();
        }
      }
    };

  const handleSubmit = async () => {
    const enteredCode = code.join("");

    if (enteredCode.length < 6) {
      dispatch({
        type: "SET_SNACKBAR",
        payload: {
          isOpen: true,
          message: "Por favor, preencha todos os campos do código.",
          type: "error",
        },
      });
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/verify-code`,
        {
          email,
          code: enteredCode,
        }
      );

      if (response.status === 200) {
        history.push("/create-new-password");
      } else {
        dispatch({
          type: "SET_SNACKBAR",
          payload: {
            isOpen: true,
            message: "Código inválido. Tente novamente",
            type: "error",
          },
        });
      }
    } catch (error: any) {
      console.error("Erro na verificação do código:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        alert(error.response.data.message);
      } else {
        dispatch({
          type: "SET_SNACKBAR",
          payload: {
            isOpen: true,
            message: "Erro ao verificar o código. Tente novamente.",
            type: "error",
          },
        });
      }
    }
  };

  return (
    <>
      <Box className={classes.root}>
        <Container className={classes.container}>
          <Paper elevation={6} className={classes.paper}>
            <Typography variant="h6" gutterBottom>
              Informe o código que você recebeu por e-mail
            </Typography>

            <Box className={classes.stepIndicator}>
              <div />
              <div />
              <div />
            </Box>

            <Typography variant="body2" gutterBottom>
              Enviamos um código de segurança para <strong>{email}</strong>.
              Esse envio pode levar alguns minutos.
            </Typography>

            <Typography variant="body2" style={{ marginTop: 24 }}>
              Código
            </Typography>

            <Box className={classes.codeContainer}>
              {code.map((value, index) => (
                <TextField
                  id={`code-input-${index}`} 
                  key={index}
                  variant="standard"
                  inputProps={{ maxLength: 1 }}
                  value={value}
                  onChange={handleChange(index)}
                  className={classes.codeInput}
                  InputProps={{
                    style: { color: "#000" },
                  }}
                />
              ))}
            </Box>

            <Button fullWidth className={classes.button} onClick={handleSubmit} disabled={code.join("").length < 6}>
              Próximo
            </Button>

            <Typography variant="subtitle2" gutterBottom>
              Ainda não recebeu o código?
            </Typography>

            <Box className={classes.resendBox}>
              {timer > 0 ? (
                <p style={{ fontFamily }}>Aguarde {timer}s</p>
              ) : (
                <Button                  
                  fullWidth
                  className={classes.button}
                  style={{ textTransform: "none" }}
                  onClick={handleResendCode}
                  variant="outlined"
                >
                  Reenviar código
                </Button>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>
      <SnackBar
        children={<>{passwords.snackbar.message}</>}
        open={passwords.snackbar.isOpen}
        feedbackType={passwords.snackbar.type}
        setOpen={() => {}}
        dispatch={() =>
          dispatch({ type: "SET_SNACKBAR", payload: { isOpen: false } })
        }
      />
    </>
  );
};

export default EmailCodeVerificationPage;
